<template>
  <form @submit.prevent="">
    <z-dialog :show="isVisible" :modal-size="modalSize">
      <template #header>
        <div class="custom-header">
          <div class="row">
            <div class="col-md-11">
              <h4 class="modal-title">
                {{ title }}
              </h4>
            </div>
            <div class="col-md-1">
              <a href="#" class="custom-close" @click="close">
                <i class="fa fa-times"></i>
              </a>
            </div>
          </div>
        </div>
      </template>
      <template #body>
        <div class="hostWrapper">
          <z-tabs v-model="currentTab" :tabs="tabs" @change="setTab" />
          <div v-if="currentTab === 'Basic'" class="p-2">
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label">Number:</span>
              </div>
              <div class="col">
                <div class="row">
                  <div class="col">
                    {{ account.acctNum }}
                  </div>
                  <div class="col-2 custom-id" title="LaunchPad Firm Id">
                    {{ account.firm }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label">Name:</span>
              </div>
              <div class="col">
                {{ account.acctName }}
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label">Created On:</span>
              </div>
              <div class="col">
                {{ formatDate(account.createTime) }}
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label">Partner:</span>
              </div>
              <div class="col">
                {{ formatSource(account.source) }}
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label">Location:</span>
              </div>
              <div class="col">
                <div class="row">
                  <div class="col">
                    {{ account.firmName }}
                  </div>
                  <div class="col-2 custom-id" title="App Firm Id">
                    {{ account.appFirm }}
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label"># Objects:</span>
              </div>
              <div class="col">
                {{ account.numObjects }}
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label"># Bytes:</span>
              </div>
              <div class="col">
                {{ account.numBytes }}
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label">Account Id:</span>
              </div>
              <div class="col">
                {{ account.acctId }}
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label">Account Key:</span>
              </div>
              <div class="col">
                {{ account.acctKey }}
              </div>
            </div>
            <div v-if="account.firmName === null || account.firmName.trim() === ''" class="row mt-4">
              <div class="col">
                <span class="custom-label text-danger">Notice</span>
                <p>Location could not be found. This Wasabi account is a good candidate for removal.</p>
              </div>
              <div class="col-2">
                <button v-if="!isFound" class="btn btn-primary btn-sm mt-3" :disabled="isSearching" @click="searchAccount(account.acctName)">
                  <i v-if="isSearching" class="fas fa-spinner fa-spin"></i>
                  Search
                </button>
                <button v-else class="btn btn-primary btn-sm mt-3" @click="apply">Apply Found</button>
              </div>
            </div>
            <div v-if="isFound" class="row mt-2">
              <div class="col custom-inline">
                <span class="custom-label me-2">Partner:</span>
                <span>{{ locationPartner }}</span>
              </div>
              <div class="col custom-inline">
                <span class="custom-label me-2">Firm Id:</span>
                <span>{{ locationFirm }}</span>
              </div>
              <div class="col custom-inline">
                <span class="custom-label me-2">Inactive:</span>
                <span :class="{ 'text-danger custom-bold': locationInactive }">{{ locationInactive }}</span>
              </div>
            </div>
            <div v-if="isFound" class="row mt-2">
              <div class="col">
                <ul class="custom-list">
                  <li v-for="(item, idx) in preferences" :key="idx">
                    <div class="row">
                      <div class="col-3">
                        <span>{{ item.firmPreferenceName }}</span>
                      </div>
                      <div class="col">
                        <span>{{ item.preference }}</span>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div v-if="currentTab === 'Portal'" class="p-2">
            <div class="row mt-2">
              <div class="col">
                <p>
                  Use the following credentials when accessing this location's Wasabi account through the
                  <a href="https://console.wasabisys.com/dashboard" target="_blank">Wasabi Portal</a>.
                </p>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label">Name:</span>
              </div>
              <div class="col custom-inline">
                {{ account.acctName }}
                <div @click="copyInfo('name')">
                  <i class="far fa-copy ms-2 custom-copy"></i>
                  <span v-if="isCopiedName" class="ms-2 custom-copied">copied!</span>
                </div>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-2">
                <span class="custom-label">Password:</span>
              </div>
              <div class="col custom-inline">
                {{ account.acctPwd }}
                <div @click="copyInfo('pwd')">
                  <i class="far fa-copy ms-2 custom-copy"></i>
                  <span v-if="isCopiedPwd" class="ms-2 custom-copied">copied!</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="currentTab === 'Buckets'" class="p-2">
            <div v-if="isCreateBucket" class="row">
              <div class="col">
                <span class="custom-label text-danger">Notice</span>
                <p>Missing a storage bucket. Recommend creating a bucket.</p>
              </div>
              <div class="col-2">
                <button class="btn btn-primary btn-sm mt-2" @click="createStorageBucket">Create Bucket</button>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col">
                <div class="row">
                  <div class="col custom-header-label">Bucket Name</div>
                  <div class="col-2 custom-header-label text-center"># Objects</div>
                  <div class="col-2 custom-header-label text-center"># Bytes</div>
                </div>
                <ul class="custom-list">
                  <li v-for="(bucket, idx) of buckets" :key="idx">
                    <div class="row">
                      <div class="col">
                        <a href="#" @click="getContents(bucket)">{{ bucket.name }}</a>
                      </div>
                      <div class="col-2 text-center">{{ bucket.numObjects }}</div>
                      <div class="col-2 text-center">{{ bucket.numBytes }}</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <bucket-contents v-if="isAny" @get-more="getContents(currentBucket)" @get-item="getBucketItem" />
                <ul v-else-if="isShowContents" class="custom-list">
                  <li>
                    <div class="row">
                      <div class="col text-center">
                        <span>No items found</span>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div v-if="currentTab === 'Utilization'" class="p-2">
            <div class="row">
              <div class="col">
                <div class="input-group mb-3">
                  <span class="input-group-text">Start:</span>
                  <input v-model="utilizationStart" type="date" class="form-control" />
                  <span class="input-group-text">End:</span>
                  <input v-model="utilizationEnd" type="date" class="form-control" />
                  <button class="btn btn-primary" @click="getUtilization">Fetch</button>
                </div>
              </div>
              <div class="col-4"></div>
            </div>
            <area-line :chart-options="optionsUtilization" />
          </div>
          <div v-if="currentTab === 'Invoices'" class="p-2">
            <account-invoices />
          </div>
        </div>
      </template>
      <template #footer>
        <div class="custom-footer">
          <div class="col-1"></div>
          <div class="col">
            <z-message v-model="message" class="alert alert-warning mt-2" />
          </div>
          <div class="col custom-right-align">
            <button type="button" class="btn btn-light me-2" @click="edit()">Edit</button>
            <button type="button" class="btn btn-primary" @click="close()">Okay</button>
          </div>
        </div>
      </template>
    </z-dialog>
  </form>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { wasabiStore } from '@/stores/wasabi'
import ZDialog from '@/components/utilities/elements/Dialog.vue'
import ZTabs from '@/components/utilities/elements/Tabs.vue'
import ZMessage from '@/components/utilities/elements/Message.vue'
import BucketContents from './BucketContents.vue'
import AccountInvoices from './AccountInvoices.vue'
import AreaLine from '@/components/utilities/charts/AreaLine.vue'
import globals from '@/plugins/globals'
import moment from 'moment'
import Highcharts from 'highcharts'
export default {
  name: 'DialogAccount',
  components: {
    ZDialog,
    ZTabs,
    ZMessage,
    BucketContents,
    AccountInvoices,
    AreaLine
  },
  props: {
    modelValue: Object,
    showFirm: Object
  },
  emits: ['closing', 'edit', 'applied'],
  data() {
    return {
      modalSize: 'modal-md',
      isSave: false,
      currentTab: 'Basic',
      message: null,
      currentBucket: null,
      isShowContents: false,
      testOptions: {
        title: 'Utilization Trend',
        subtitle: 'Number of Billable Objects',
        series: [
          {
            data: [0, 1, 2, 3, 4]
          }
        ]
      },
      isCopiedName: false,
      isCopiedPwd: false
    }
  },
  mounted() {
    this.currentTab = 'Basic'
    this.setAccount({
      name: 'Paginate',
      value: 'paginate'
    })
    this.isShowContents = false
  },
  watch: {
    api_message() {
      console.log('api_message', this.api_message)
      this.message = this.api_message
    }
  },
  computed: {
    ...mapState(wasabiStore, [
      'account',
      'buckets',
      'utilization',
      'contents',
      'continuationToken',
      'utilizationRange',
      'api_message',
      'preferences',
      'loading'
    ]),
    isVisible() {
      return this.modelValue !== null
    },
    tabs() {
      let _tabs = ['Basic', 'Portal', 'Buckets', 'Utilization', 'Invoices']
      return _tabs
    },
    title() {
      let _title = 'Wasabi Account'
      return _title
    },
    ids() {
      let _ids = ''
      if (this.modelValue) {
        if (!this.modelValue.isAdd) {
          _ids = `${this.modelValue.user}`
        }
      }
      return _ids
    },
    firm() {
      let _id = null
      if (this.account) {
        _id = this.account.firm
      }
      return _id
    },
    isFirm() {
      let _is = false
      if (this.account) {
        if (this.account.firm) {
          _is = true
        }
      }
      return _is
    },
    contactData() {
      let _data = null
      if (this.contact) {
        _data = {
          firm: this.contact.firm,
          contact: this.contact.contact
        }
      }
      return _data
    },
    isAny() {
      let _is = false
      if (this.contents) {
        _is = this.contents.length > 0
      }
      return _is
    },
    isCreateBucket() {
      let _is = false
      if (this.isFirm || (this.account.acctId !== null && this.account.acctKey !== null)) {
        if (this.buckets) {
          _is = this.buckets.length === 0
        } else {
          _is = true
        }
      }
      return _is
    },
    optionsUtilization() {
      let data = null
      if (this.utilization) {
        let seriesObjects = this.utilization.map((element) => {
          return [element.NumBillableObjects]
        })
        let seriesBytes = this.utilization.map((element) => {
          return [element.MetadataStorageSizeBytes]
        })
        let xAxis = this.utilization.map((element) => {
          return this.formatDate(element.CreateTime, 'mmdd')
        })
        data = {
          chart: {
            zooming: {
              type: 'xy'
            }
          },
          title: 'Utilization Trend',
          xAxis: {
            categories: xAxis
          },
          yAxis: [
            {
              // Primary yAxis
              labels: {
                format: '{value}',
                style: {
                  color: Highcharts.getOptions().colors[1]
                }
              },
              title: {
                text: '# objects',
                style: {
                  color: Highcharts.getOptions().colors[1]
                }
              }
            },
            {
              // Secondary yAxis
              title: {
                text: 'bytes',
                style: {
                  color: Highcharts.getOptions().colors[0]
                }
              },
              labels: {
                format: '{value}',
                style: {
                  color: Highcharts.getOptions().colors[0]
                }
              },
              opposite: true
            }
          ],
          series: [
            {
              name: '# objects',
              data: seriesObjects,
              type: 'column',
              yAxis: 1
            },
            {
              name: '# bytes',
              data: seriesBytes,
              type: 'spline'
            }
          ]
        }
      }
      return data
    },
    utilizationStart: {
      get() {
        let _start = null
        if (this.utilizationRange) {
          _start = moment(this.utilizationRange.start).format('YYYY-MM-DD')
        }
        return _start
      },
      set(value) {
        this.message = null
        if (value === '') {
          value = moment().format('YYYY-MM-DD')
        }
        if (value > this.utilizationEnd) {
          this.message = 'Start date must be less than end date.'
        } else {
          this.setUtilizationRange({
            name: 'start',
            value: value
          })
        }
      }
    },
    utilizationEnd: {
      get() {
        let _end = null
        if (this.utilizationRange) {
          _end = moment(this.utilizationRange.end).format('YYYY-MM-DD')
        }
        return _end
      },
      set(value) {
        this.message = null
        if (value === '') {
          value = moment().format('YYYY-MM-DD')
        }
        if (value < this.utilizationStart) {
          this.message = 'End date must be greater than start date.'
        } else {
          this.setUtilizationRange({
            name: 'end',
            value: value
          })
        }
      }
    },
    isSearching() {
      let _is = false
      if (this.loading) {
        _is = this.loading.search
      }
      return _is
    },
    isFound() {
      let _is = false
      if (this.preferences) {
        _is = this.preferences.length > 0
      }
      return _is
    },
    locationPartner() {
      let _data = null
      if (this.preferences) {
        if (this.preferences.length > 0) {
          _data = this.preferences[0].source
        }
      }
      return _data
    },
    locationFirm() {
      let _data = null
      if (this.preferences) {
        if (this.preferences.length > 0) {
          _data = this.preferences[0].firm
        }
      }
      return _data
    },
    locationInactive() {
      let _data = false
      if (this.preferences) {
        if (this.preferences.length > 0) {
          _data = this.preferences[0].isInactive
        }
      }
      return _data
    }
  },
  methods: {
    ...mapActions(wasabiStore, [
      'getBucketContents',
      'setAccount',
      'getPresignedUrl',
      'createBucket',
      'setUtilizationRange',
      'getAccountUtilization',
      'getAccountBuckets',
      'getInvoices',
      'searchAccount',
      'saveAccount'
    ]),
    formatDate(date, type = 'datetime') {
      return globals.formatDate(date, type)
    },
    formatSource(data) {
      let source = null
      if (data) {
        if (data === 'Calibrate' || data === 'TestDrive') {
          source = `Legacy - ${data}`
        } else {
          source = data
        }
      }
      return source
    },
    setTab(value) {
      if (value === 'Invoices') {
        this.getInvoices(this.account.acctNum)
      }
      this.currentTab = value
    },
    async getContents(bucket) {
      let data = JSON.parse(JSON.stringify(bucket))
      data.maxKeys = 10
      data.continuationToken = this.continuationToken
      this.currentBucket = data
      await this.getBucketContents(bucket)
      this.isShowContents = true
    },
    async getBucketItem(item) {
      if (this.account.acctId === null || this.account.acctKey === null) {
        this.message = 'Account ID and Key are required.'
        return
      }
      let data = {
        id: this.account.acctId,
        key: this.account.acctKey,
        region: this.account.region,
        bucket: this.currentBucket.name,
        filename: item.key
      }
      let urlInfo = await this.getPresignedUrl(data)
      if (urlInfo) {
        if (urlInfo.url) {
          window.open(urlInfo.url, '_blank')
        } else {
          this.message = JSON.stringify(urlInfo)
        }
      }
    },
    async createStorageBucket() {
      if (this.isFirm) {
        await this.createBucket({
          firm: this.account.firm
        })
      } else {
        await this.createBucket({
          id: this.account.acctId,
          key: this.account.acctKey,
          region: this.account.source,
          acctNum: this.account.acctNum.toString()
        })
      }
      await this.getAccountBuckets(this.account.acctNum)
    },
    async getUtilization() {
      let data = {
        accountNumber: this.account.acctNum,
        start: moment(this.utilizationRange.end).subtract(180, 'day').format('YYYY-MM-DD'),
        end: moment(this.utilizationRange.end).format('YYYY-MM-DD')
      }
      console.log('Getting Utilization', data)
      await this.getAccountUtilization(data)
    },
    copyInfo(type) {
      let _info = null
      this.isCopiedName = false
      this.isCopiedPwd = false
      if (type === 'name') {
        _info = this.account.acctName
        this.isCopiedName = true
        setTimeout(() => {
          this.isCopiedName = false
          this.isCopiedPwd = false
        }, 1000)
      } else if (type === 'pwd') {
        _info = this.account.acctPwd
        this.isCopiedPwd = true
        setTimeout(() => {
          this.isCopiedName = false
          this.isCopiedPwd = false
        }, 1000)
      }
      navigator.clipboard.writeText(_info)
    },
    edit() {
      this.isSave = false
      this.currentTab = 'Basic'
      this.isShowContents = false
      this.$emit('edit')
    },
    async apply() {
      console.log('apply', this.preferences)
      let data = {
        acctNum: this.account.acctNum,
        acctName: this.account.acctName,
        source: null,
        appFirm: null,
        acctPwd: null,
        firmName: null,
        acctKey: null,
        acctId: null
      }
      let found = this.preferences.find((x) => x.firmPreferenceName === 'wasabiAccountName')
      if (found) {
        data.appFirm = found.firm
        data.firmName = found.firmName
        data.source = found.source
      }
      found = this.preferences.find((x) => x.firmPreferenceName === 'wasabiAccountPassword')
      if (found) {
        data.acctPwd = found.preference
      }
      found = this.preferences.find((x) => x.firmPreferenceName === 'wasabiAccountId')
      if (found) {
        data.acctId = found.preference
      }
      found = this.preferences.find((x) => x.firmPreferenceName === 'wasabiAccountKey')
      if (found) {
        data.acctKey = found.preference
      }
      console.log('save apply', data)
      await this.saveAccount(data)
      this.$emit('applied')
    },
    close() {
      this.isSave = false
      this.currentTab = 'Basic'
      this.isShowContents = false
      this.$emit('closing')
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-header {
  width: 100%;
}
.custom-textarea {
  height: 200px;
}
.custom-close {
  float: right;
  margin-right: 10px;
  color: #000;
  cursor: pointer;
}
.custom-footer {
  width: 100%;
  display: inline-flex;
}
.custom-right-align {
  text-align: right;
}
.hostWrapper {
  max-height: 650px;
  overflow-x: hidden;
  overflow-y: auto;
  height: 600px;
}
.custom-header-label {
  font-weight: 600;
}
.custom-list {
  list-style-type: none;
  padding-inline-start: 0px;
  li {
    margin-top: 2px;
    margin-bottom: 2px;
  }
}
.custom-label {
  font-weight: 500;
}
.custom-id {
  cursor: pointer;
}
.custom-inline {
  display: inline-flex;
}
.custom-copy {
  cursor: pointer;
  color: #0d6efd;
}
.custom-copied {
  font-size: 0.8rem;
  font-weight: 500;
  color: #198754;
}
.custom-bold {
  font-weight: 600;
}
</style>
